import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ENDPOINTS, fetchWithLog } from '../config';
import './Chatbot.css';

const PAGE_ROUTES = 'Available pages: Home (/), About (/about), Services (/services), Skills (/skills), Portfolio (/portfolio), Contact (/contact). When recommending a page, use markdown links: [Page Name](/path). Example: "Check the [About](/about) page" or "Use [Contact](/contact) to get in touch."';

function renderMessageWithLinks(text, onLinkClick) {
  if (!text || typeof text !== 'string') return text;
  const parts = [];
  const regex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    parts.push({ type: 'text', content: text.slice(lastIndex, match.index) });
    const url = match[2];
    const isInternal = url.startsWith('/') && !url.startsWith('//');
    parts.push({ type: 'link', text: match[1], url, internal: isInternal });
    lastIndex = regex.lastIndex;
  }
  parts.push({ type: 'text', content: text.slice(lastIndex) });
  return parts.map((p, i) => {
    if (p.type === 'text') return <span key={i}>{p.content}</span>;
    if (p.internal) return <Link key={i} to={p.url} className="chatbot-msg-link" onClick={onLinkClick}>{p.text}</Link>;
    return <a key={i} href={p.url} target="_blank" rel="noopener noreferrer" className="chatbot-msg-link" onClick={onLinkClick}>{p.text}</a>;
  });
}

const stripHtml = (html) => (html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

function getRelevantEndpoints(prompt) {
  const q = prompt.toLowerCase();
  const needs = new Set();

  const about = /\b(about|bio|biography|who|introduce|yourself|person|owner|overview)\b/;
  const skills = /\b(skill|technology|tech|expertise|language|framework|tool|stack|proficient)\b/;
  const services = /\b(service|offer|what do you do|hire|work with|build for me)\b/;
  const projects = /\b(project|portfolio|work|built|created|app|website|developed)\b/;
  const education = /\b(education|degree|school|university|study|qualification|certification)\b/;
  const broad = /\b(site|website|everything|tell me about|what's on|what can you|general|overview)\b/;

  if (broad.test(q)) return ['about', 'skills', 'services', 'projects', 'projectCategories', 'education'];
  if (about.test(q)) needs.add('about');
  if (skills.test(q)) needs.add('skills');
  if (services.test(q)) needs.add('services');
  if (projects.test(q)) {
    needs.add('projects');
    needs.add('projectCategories');
  }
  if (education.test(q)) needs.add('education');

  if (needs.size === 0) needs.add('about');
  return Array.from(needs);
}

async function fetchRelevantContext(endpointNames) {
  const fetches = [];
  if (endpointNames.includes('about')) fetches.push(fetchWithLog(ENDPOINTS.about).then((r) => r.json()));
  if (endpointNames.includes('skills')) fetches.push(fetchWithLog(ENDPOINTS.skills).then((r) => r.json()));
  if (endpointNames.includes('services')) fetches.push(fetchWithLog(ENDPOINTS.services).then((r) => r.json()));
  if (endpointNames.includes('projects')) fetches.push(fetchWithLog(ENDPOINTS.projects).then((r) => r.json()));
  if (endpointNames.includes('projectCategories')) fetches.push(fetchWithLog(ENDPOINTS.projectCategories).then((r) => r.json()));
  if (endpointNames.includes('education')) fetches.push(fetchWithLog(ENDPOINTS.education).then((r) => r.json()));

  const results = await Promise.all(fetches);
  let idx = 0;
  const context = {};

  if (endpointNames.includes('about')) {
    const d = results[idx++];
    context.biography = d?.[0]?.biography ?? '';
  }
  if (endpointNames.includes('skills')) {
    const skillsData = results[idx++];
    const skillsTree = {};
    (skillsData || []).forEach((s) => {
      if (s.vid === 'skills') skillsTree[s.tid] = { title: s.title, subcats: {} };
      else if (s.vid === 'skills_subcategory' && s.category) {
        if (!skillsTree[s.category]) skillsTree[s.category] = { title: '', subcats: {} };
        skillsTree[s.category].subcats[s.tid] = { title: s.title, items: [] };
      } else if (s.vid === 'skills_items' && s.category && s.subcategory) {
        const cat = skillsTree[s.category];
        if (cat?.subcats[s.subcategory]) cat.subcats[s.subcategory].items.push(s.title);
      }
    });
    const lines = [];
    Object.values(skillsTree).forEach((cat) => {
      Object.values(cat.subcats || {}).forEach((sub) => {
        if (sub.items?.length) lines.push(`- ${cat.title} > ${sub.title}: ${sub.items.join(', ')}`);
      });
    });
    context.skillsText = lines.join('\n');
  }
  if (endpointNames.includes('services')) {
    const d = results[idx++];
    context.servicesText = (d || []).map((s) => `- ${s.labelText}: ${s.descriptionText}`).join('\n');
  }
  if (endpointNames.includes('projects')) {
    const d = results[idx++];
    context.projectsText = (d || [])
      .map((p) => {
        const parts = [`- ${p.title} (${p.category})`];
        if (p.summary) parts.push(`: ${p.summary}`);
        if (p.technologies) parts.push(` [${p.technologies}]`);
        return parts.join('');
      })
      .join('\n');
  }
  if (endpointNames.includes('projectCategories')) {
    const d = results[idx++];
    context.categoriesText = (d || []).filter((c) => c.is_visible).map((c) => c.title).join(', ');
  }
  if (endpointNames.includes('education')) {
    const d = results[idx++];
    const ed = d?.[0] ?? {};
    context.educationText = [ed.education, ed.education2].filter(Boolean).map(stripHtml).join(' ');
  }

  return context;
}

const DEFAULT_PROMPT = `You are a helpful assistant for this portfolio website, which exists to sell the owner's services. Your goal is to help visitors understand what's offered and encourage them to get in touch or hire. Answer questions using ONLY the following context from the site. Be friendly, concise, and professional. When relevant, naturally suggest contacting or hiring. If the answer is not in the context, say you don't have that information and suggest exploring the relevant section or the Contact / Let's Talk page.`;

function buildSystemPrompt(context, basePrompt = DEFAULT_PROMPT) {
  const parts = [basePrompt];

  if (context.biography) parts.push(`\n\n## About / Biography\n${context.biography}`);
  if (context.educationText) parts.push(`\n\n## Education\n${context.educationText}`);
  if (context.skillsText) parts.push(`\n\n## Skills\n${context.skillsText}`);
  if (context.servicesText) parts.push(`\n\n## Services Offered\n${context.servicesText}`);
  if (context.projectsText) parts.push(`\n\n## Projects\n${context.projectsText}`);
  if (context.categoriesText) parts.push(`\n\n## Portfolio Categories\n${context.categoriesText}`);

  parts.push(`\n\n## Contact\nVisitors can use the Contact section or "Let's Talk" to get in touch.`);
  parts.push(`\n\n## Page Links\n${PAGE_ROUTES}`);
  return parts.join('');
}

export default function Chatbot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [lastContext, setLastContext] = useState(null);
  const [showDebug, setShowDebug] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || isLoading) return;

    const userMessage = { role: 'user', content: text };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    const lastUserMsg = messages.filter((m) => m.role === 'user').pop()?.content ?? '';
    const promptForRelevance = text.length < 20 && lastUserMsg ? `${lastUserMsg} ${text}` : text;
    const endpoints = getRelevantEndpoints(promptForRelevance);

    let basePrompt = DEFAULT_PROMPT;
    let context = {};
    try {
      const [promptRes, ctx] = await Promise.all([
        fetch(ENDPOINTS.chatbotPrompt).then((r) => r.json()),
        fetchRelevantContext(endpoints),
      ]);
      basePrompt = (promptRes?.prompt || '').trim() || DEFAULT_PROMPT;
      context = ctx;
    } catch {
      try {
        const promptRes = await fetch(ENDPOINTS.chatbotPrompt).then((r) => r.json());
        basePrompt = (promptRes?.prompt || '').trim() || DEFAULT_PROMPT;
      } catch {
        /* use DEFAULT_PROMPT */
      }
    }
    setLastContext({ ...context, _basePrompt: basePrompt });

    const systemPrompt = Object.keys(context).length
      ? buildSystemPrompt(context, basePrompt)
      : basePrompt;

    try {
      const chatMessages = [
        { role: 'system', content: systemPrompt },
        ...messages.map((m) => ({ role: m.role, content: m.content })),
        userMessage,
      ];

      const res = await fetchWithLog(ENDPOINTS.chat, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: chatMessages,
          model: 'llama-3.3-70b-versatile',
          temperature: 0.7,
          max_tokens: 1024,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || `Request failed (${res.status})`);
      }

      const content = data.choices?.[0]?.message?.content ?? 'Sorry, I could not generate a response.';
      setMessages((prev) => [...prev, { role: 'assistant', content }]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: `Sorry, something went wrong: ${err.message}. Please ensure the chat API is configured with a valid Groq API key.`,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className="chatbot-container">
      {isOpen && (
        <>
          <div className="chatbot-overlay" onClick={() => setIsOpen(false)} aria-hidden="true" />
          <div className="chatbot-window">
          <div className="chatbot-header">
            <span className="chatbot-title">Chat</span>
            <div className="chatbot-header-actions">
              {process.env.NODE_ENV === 'development' && (
                <button
                  type="button"
                  className={`chatbot-debug-toggle ${showDebug ? 'active' : ''}`}
                  onClick={() => setShowDebug(!showDebug)}
                  title="Toggle debug view"
                  aria-label="Toggle debug view"
                >
                  Debug
                </button>
              )}
              <button
                type="button"
                className="chatbot-close"
                onClick={() => setIsOpen(false)}
                aria-label="Close chat"
              >
                ×
              </button>
            </div>
          </div>
          {process.env.NODE_ENV === 'development' && showDebug && (
            <div className="chatbot-debug-panel">
              <pre className="chatbot-debug-content">
                {lastContext
                  ? buildSystemPrompt(
                      Object.fromEntries(Object.entries(lastContext).filter(([k]) => k !== '_basePrompt')),
                      lastContext._basePrompt || DEFAULT_PROMPT
                    )
                  : 'Context will appear here after you send a message.'}
              </pre>
            </div>
          )}
          <div className="chatbot-messages">
            {messages.length === 0 && (
              <div className="chatbot-welcome">
                Hi! 👋 I'm here to help you discover how we can work together. Ask me about my services, projects, or let's discuss your next idea—I'd love to hear from you!
              </div>
            )}
            {messages.map((msg, i) => (
              <div key={i} className={`chatbot-msg chatbot-msg-${msg.role}`}>
                {msg.role === 'assistant' ? renderMessageWithLinks(msg.content, () => setIsOpen(false)) : msg.content}
              </div>
            ))}
            {isLoading && (
              <div className="chatbot-msg chatbot-msg-assistant chatbot-typing">
                <span className="typing-dot" />
                <span className="typing-dot" />
                <span className="typing-dot" />
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          <div className="chatbot-input-area">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type your message..."
              rows={1}
              disabled={isLoading}
            />
            <button
              type="button"
              className="chatbot-send"
              onClick={sendMessage}
              disabled={!input.trim() || isLoading}
              aria-label="Send message"
            >
              Send
            </button>
          </div>
        </div>
        </>
      )}
      <button
        type="button"
        className={`chatbot-toggle ${isOpen ? 'chatbot-toggle-open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
      >
        {isOpen ? '×' : '💬'}
      </button>
    </div>
  );
}
